import { useEffect } from 'react';
import { create } from 'zustand';
import type { FileShareProtocolState, StorageResourceRef, StorageSasGrant } from '../../../../../types/azureStorage';
import type { HnsDialogKind, HnsDialogOptions } from '../../hns/dialogs/hnsDialogTypes';

export type StorageDialogKind = 'properties' | 'tier' | 'public-access' | 'access-policies' | 'sas'
  | HnsDialogKind; // S8: 'hns-new-directory' | 'hns-move' | 'hns-acl' | 'hns-acl-propagate'
/** `protocol` (S7): a share's protocol as the opener knows it — an analytics tag of the share policy actions only. `hns` (S8): the Data Lake dialogs' options. */
export type StorageDialogRequest = { kind: StorageDialogKind; connectionId: string; resource: StorageResourceRef; source?: 'toolbar' | 'row-menu' | 'panel' | 'chip' | 'tree' | 'mcp'; grant?: StorageSasGrant; onAcknowledged?: () => void; protocol?: FileShareProtocolState; hns?: HnsDialogOptions };
/**
 * One opening of a dialog. `instanceId` is unique per opening — also for an identical request — and the host keys the
 * dialog by it, so a replacement always mounts fresh state (a second MCP grant is shown, never the first) and the
 * replaced dialog unmounts, which cancels its in-flight request through its own request hook (review D RD2).
 */
export type StorageDialog = StorageDialogRequest & { instanceId: number };
/** What the open dialog reports (Fable F8): an unsaved draft, and / or a save in flight. */
export type StorageDialogGuard = { dirty: boolean; saving: boolean };

const CLEAN: StorageDialogGuard = { dirty: false, saving: false };

type StorageDialogState = {
  dialog: StorageDialog | null;
  guard: StorageDialogGuard;
  /** Openings that arrived while the open dialog was busy; the first opens when that dialog closes (or on View). */
  queued: StorageDialogRequest[];
  /** An opening that would discard the open dialog's draft, waiting for "Discard and open" / "Keep editing". */
  confirming: StorageDialogRequest | null;
  openDialog: (dialog: StorageDialogRequest) => void;
  closeDialog: () => void;
  reportGuard: (instanceId: number, guard: StorageDialogGuard) => void;
  viewQueued: () => void;
  confirmReplacement: () => void;
  keepEditing: () => void;
};

let openings = 0;

/**
 * The single dialog slot (Fable F8). A clean dialog is replaced at once. A busy one — unsaved draft or save in flight —
 * is never replaced silently: an agent's result (and any opening while a save runs, which another opening must never
 * cancel) waits in `queued` behind a notice; a user opening over a draft asks first (`confirming`).
 */
export const useStorageDialogStore = create<StorageDialogState>((set, get) => {
  const show = (request: StorageDialogRequest, queued = get().queued) => {
    openings += 1;
    set({ dialog: { ...request, instanceId: openings }, guard: CLEAN, confirming: null, queued });
  };
  return {
    dialog: null,
    guard: CLEAN,
    queued: [],
    confirming: null,
    openDialog: (request) => {
      const { dialog, guard, queued } = get();
      if (!dialog || (!guard.dirty && !guard.saving)) { show(request); return; }
      if (request.source === 'mcp' || guard.saving) set({ queued: [...queued, request] });
      else set({ confirming: request });
    },
    closeDialog: () => {
      const [next, ...rest] = get().queued;
      if (next) show(next, rest);
      else if (get().dialog) set({ dialog: null, guard: CLEAN, confirming: null });
    },
    reportGuard: (instanceId, guard) => {
      const current = get();
      if (current.dialog?.instanceId !== instanceId) return;
      if (current.guard.dirty !== guard.dirty || current.guard.saving !== guard.saving) set({ guard });
    },
    viewQueued: () => {
      const { queued, guard } = get();
      const next = queued[0];
      if (!next || guard.saving) return;
      if (guard.dirty) set({ confirming: next });
      else show(next, queued.slice(1));
    },
    confirmReplacement: () => {
      const { confirming, guard, queued } = get();
      if (!confirming || guard.saving) return;
      show(confirming, queued.filter((request) => request !== confirming));
    },
    keepEditing: () => set({ confirming: null }),
  };
});

/** A hosted dialog reports its draft / save state to the slot through the host's callback (Fable F8). */
export function useReportStorageDialogGuard(onGuardChange: ((guard: StorageDialogGuard) => void) | undefined, dirty: boolean, saving: boolean): void {
  useEffect(() => { onGuardChange?.({ dirty, saving }); }, [onGuardChange, dirty, saving]);
}
